import { useCallback, useState } from 'react'
import { GeckoMeasurement } from 'types'

interface ThresholdOverride {
  componentId: string | undefined
  value: GeckoMeasurement | null
}

export interface LtaThreshold {
  /** The value to show in the LTA threshold field. */
  threshold: GeckoMeasurement | null
  setThreshold: (value: GeckoMeasurement | null) => void
  /**
   * Non-null when `threshold` is the `t_min` seed rather than a persisted or
   * hand-entered value -- drives the provenance hint under the input.
   */
  seededTMin: GeckoMeasurement | null
}

/**
 * Resolves the LTA threshold shown by `FfsBinningStep`:
 *
 *   the user's own entry  >  the persisted value  >  the `t_min` seed
 *
 * Only the user's entry is state. Everything else is derived on render, which
 * is what makes the two failure modes here structurally impossible rather than
 * merely guarded against:
 *
 *  - SWR revalidation can't clobber a typed-but-unsaved value, because there is
 *    no synced state for it to overwrite. (The previous effect-based hydration
 *    re-ran on every `savedFfsConfig` identity change and silently reset the
 *    field.)
 *  - Switching component tabs can't strand the previous component's number,
 *    because the override carries the `componentId` it was made for. The step
 *    re-scopes in place rather than remounting, so an unkeyed override would
 *    leak across tabs.
 */
export function useLtaThreshold({
  componentId,
  savedThreshold,
  tMinSeed,
}: {
  componentId: string | undefined
  /** Persisted `localThinAreaThreshold` from the FFS configuration entry. */
  savedThreshold: GeckoMeasurement | null
  /** `ltaThresholdSeedFromTMin(...)` for the component in scope. */
  tMinSeed: GeckoMeasurement | null
}): LtaThreshold {
  const [override, setOverride] = useState<ThresholdOverride>()

  const activeOverride =
    override && override.componentId === componentId ? override : undefined

  const setThreshold = useCallback(
    (value: GeckoMeasurement | null) => setOverride({ componentId, value }),
    [componentId]
  )

  return {
    threshold: activeOverride
      ? activeOverride.value
      : (savedThreshold ?? tMinSeed),
    setThreshold,
    seededTMin: !activeOverride && savedThreshold === null ? tMinSeed : null,
  }
}
